import { flowRight, first, get, isEmpty, isNil } from 'lodash';
import I18n from 'i18n-js';
import React from 'react';
import { ChildProps, MutationFn } from 'react-apollo';

import LocationForm from './location_form';
import OfferCreateFormFields from './offer_create_form_fields';
import { connectCreateOfferFormQuery, connectCreateInitialOfferMutation } from './offer_operations';
import { withUserContext, IUserContext } from '../components/user_context_provider';
import bind from '../bind';
import { convertCurrency, convertMoney } from '../convert_currency';
import { shippingPriceDataForMethod } from '../components/listing_shipping_display';
import * as elog from '../elog';
import { parseAmount, formatNumber } from '../money';
import { offerTotal } from './offer_calculator';
import { isUSSubregion } from './shipping_region';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import { UserInputOffer, IOfferLocation } from './negotiation_types';
import { RCAlertBox } from '../rc/RCAlertBox';
import { NON_FUNCTIONING, BRAND_NEW } from '../condition_helpers';
import { canViewListingPriceRecommendations } from '../user_context_helpers';

import {
  CommonsOffersNewOfferForm,
  CommonsOffersCreateOfferForm,
  core_apimessages_Channel,
  core_apimessages_Money as IMoney,
  Input_core_apimessages_CreateNegotiationRequest,
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_BumpKey,
  Input_core_apimessages_BumpKey,
  core_apimessages_ShippingMethod as IShippingMethod,
} from '../rql';

interface IOfferItem {
  listingId: string;
  shippingPrice?: IMoney;
  quantity: number;
  price: IMoney;
}

interface IExternalProps {
  listingId: string;
  recipientUuid?: string; // Must be passed if isSeller == true
  isSeller: boolean;
  shopChannel: core_apimessages_Channel;
  onSuccess: (negotiation: CommonsOffersCreateOfferForm.Negotiations) => void;
  onError?: (error: any) => void;
  onCancel?: () => void;
  isDisplayed: boolean; // Used for skipping initial gql query
  offer?: UserInputOffer; // offer and onChange should be passed to use as a controlled component
  onChange?: (offer: Partial<UserInputOffer>) => void;
  messageInput?: React.ReactNode;
  additionalMutationParams?: Partial<Input_core_apimessages_CreateNegotiationRequest>;
  bumpKey?: core_apimessages_BumpKey;
}

interface IState {
  error: string;
  isFormSaving: boolean;
  offer: UserInputOffer;
  location: IOfferLocation;
  isEditingLocation: boolean;
}

interface ApolloProps {
  createInitialOffer: MutationFn<CommonsOffersCreateOfferForm.Mutation, CommonsOffersCreateOfferForm.Variables>;
}

export type IProps = ChildProps<IExternalProps, CommonsOffersNewOfferForm.Query> & ApolloProps & IUserContext;

const RECOMMENDED_PRICE_DROP_MIN = 3; // percent
const RECOMMENDED_PRICE_DROP_MAX = 15; // percent

const defaultState = (): IState => {
  return {
    error: '',
    isFormSaving: false,
    offer: {
      price: '',
      message: '',
      quantity: 1,
    },
    location: { postalCode: '', shippingRegionCode: '' },
    isEditingLocation: true,
  };
};

const formatBumpKey = (bumpKey: core_apimessages_BumpKey): Input_core_apimessages_BumpKey => {
  if (!bumpKey?.key) {
    return null;
  }

  return {
    key: bumpKey.key,
  };
};

export class OfferCreateForm extends React.Component<IProps, IState> {
  state = defaultState();

  componentDidMount() {
    this.setInitialLocation({});
  }

  componentDidUpdate(prevProps) {
    this.setInitialLocation(prevProps);
  }

  missingRequiredLocation(address: IOfferLocation) {
    if (this.props.isSeller) { return false; }
    return !(address && address.postalCode && address.shippingRegionCode);
  }

  setInitialLocation(prevProps) {
    const shippingInfo = get(this.props, 'data.listing.shipping');
    const prevShippingInfo = get(prevProps, 'data.listing.shipping');
    if (!prevShippingInfo && shippingInfo) {
      const location: IOfferLocation = {
        postalCode: get(shippingInfo, 'shippingAddress.postalCode', ''),
        shippingRegionCode: shippingInfo.shippingRegionCode,
      };
      this.setState({
        location,
        isEditingLocation: this.missingRequiredLocation(location),
      });
    }
  }

  get isLoading() {
    return get(this.props, 'data.loading');
  }

  get shopCurrency() {
    return this.props.data.listing.shop.currency;
  }

  get errorLoading() {
    return get(this.props, 'data.error');
  }

  get exchangeRates() {
    return get(this.props, 'data.exchangeRates.rates', []);
  }

  get offerError() {
    return get(this.props, 'data.error.message');
  }

  get shippingPrice() {
    if (this.props.isSeller && !isNil(this.offerState.shippingPrice)) {
      return this.offerState.shippingPrice;
    }

    const standardShippingData = shippingPriceDataForMethod(this.props.data.listing, IShippingMethod.SHIPPED);
    if (standardShippingData?.carrierCalculated) { return undefined; }
    if (!standardShippingData?.rate?.amount) { return undefined; }
    return formatNumber(Number(standardShippingData.rate.amount));
  }

  get quantity(): number {
    if (this.props.isSeller && !isNil(this.offerState.quantity)) {
      return this.offerState.quantity;
    }

    return 1;
  }

  get offerState(): UserInputOffer {
    return {
      ...this.state.offer,
      ...this.props.offer,
    };
  }

  get offer(): UserInputOffer {
    return {
      ...this.offerState,
      shippingPrice: this.shippingPrice,
      quantity: this.quantity,
    };
  }

  get buyerHasAtLeastOneAcceptedOffer() {
    return get(this.props.data, 'me.offerCount.total') > 0;
  }

  get disableSaveButton() {
    return this.isLoading ||
      this.state.isFormSaving ||
      !this.offer.price ||
      this.missingRequiredLocation(this.state.location);
  }

  get negotiationParty() {
    return this.props.isSeller ? NegotiationParty.SELLER : NegotiationParty.BUYER;
  }

  get showTaxAtCheckoutNotice(): boolean {
    const shippingRegionCode = get(this.props.data.listing, 'shipping.shippingRegionCode');

    return this.negotiationParty === NegotiationParty.BUYER &&
      (!shippingRegionCode || isUSSubregion(shippingRegionCode));
  }

  @bind
  async createNegotiation(): Promise<CommonsOffersCreateOfferForm.Negotiations> {
    const shopCurrencyOfferPrice = convertCurrency(
      this.offerCurrency,
      this.props.data.listing.shop.currency,
      this.exchangeRates,
      this.offer.price,
    );

    const offerItem: IOfferItem = {
      listingId: String(this.props.listingId),
      quantity: this.quantity,
      price: shopCurrencyOfferPrice,
    };

    if (this.props.isSeller) {
      offerItem.shippingPrice = parseAmount(
        this.shippingPrice,
        this.props.data.listing.shop.currency,
      );
    }

    let shippingRegionCodeForBuyer = null;
    if (!this.props.isSeller) {
      shippingRegionCodeForBuyer = this.state.location.shippingRegionCode;
    }

    const input: Input_core_apimessages_CreateNegotiationRequest = {
      recipientUuid: this.props.recipientUuid,
      message: this.offer.message,
      offerItems: [offerItem],
      shippingRegionCode: shippingRegionCodeForBuyer,
      postalCode: this.state.location.postalCode,
      bumpKey: formatBumpKey(this.props.bumpKey),
      ...(this.props.additionalMutationParams || {}),
    };

    const result = await this.props.createInitialOffer({ variables: { input } });
    return get(result, 'data.createNegotiation.negotiations.0');
  }

  @bind
  handleOpenLocationForm() {
    this.setState({ isEditingLocation: true });
  }

  calculateDiscountPercent() {
    return Math.round((1 - (parseFloat(this.offer.price) / parseFloat(this.props.data.listing.price.amount))) * 100);
  }

  @bind
  async handleSubmit(e) {
    e.preventDefault();
    this.setState({ isFormSaving: true, error: null });

    try {
      const response = await this.createNegotiation();

      trackEvent({
        eventName: MParticleEventName.SubmittedOffer,
        listingId: this.props.listingId,
        discountPercent: this.calculateDiscountPercent(),
      });

      this.setState({ isFormSaving: false });

      this.props.onSuccess(response);
    } catch (errors) {
      const { message } = first(get(errors, 'graphQLErrors', [{ message: '' }]));
      elog.error('offer.create', { errors }, message);
      this.setState({ error: message, isFormSaving: false });
      if (this.props.onError) this.props.onError(errors);
    }
  }

  get isControlledComponent() {
    return !!this.props.offer;
  }

  @bind
  handleOfferChange(offer: Partial<UserInputOffer>) {
    if (this.isControlledComponent) {
      this.props.onChange(offer);
    } else {
      this.setState({
        offer: {
          ...this.state.offer,
          ...offer,
        },
      });
    }
  }

  refetchOnShippingRegionCodeChange(previousShippingRegionCode: string) {
    const { shippingRegionCode } = this.state.location;
    if (shippingRegionCode !== previousShippingRegionCode) {
      this.props.data.refetch({ shippingRegionCode, usePrimaryShippingAddress: false });
    }
  }

  @bind
  handleLocationChange(location: IOfferLocation) {
    const previousShippingRegionCode = this.state.location.shippingRegionCode;

    this.setState(
      {
        location: {
          ...this.state.location,
          ...location,
        },
      },
      () => this.refetchOnShippingRegionCodeChange(previousShippingRegionCode));
  }

  renderListingPrice() {
    return (
      <div className="offers-form__original-price">
        {I18n.t('commons.offers.originalPrice', { price: this.props.data.listing.price.display })}
      </div>
    );
  }

  renderLocation() {
    if (this.isLoading || this.props.isSeller) {
      return null;
    }

    return (
      <div className="mtb-1 bdt-1 bdb-1 bd-primary scaling-ptb-2">
        <LocationForm
          isEditing={this.state.isEditingLocation}
          countries={get(this.props.data, 'countries.countries', [])}
          location={this.state.location}
          onLocationChange={this.handleLocationChange}
          onEditLocation={this.handleOpenLocationForm}
        />
        {this.renderLocationMessage()}
      </div>
    );
  }

  renderLocationMessage() {
    const { shipping, shop } = this.props.data.listing;

    if (this.props.data.loading) { return null; }

    if (shipping.localPickupOnly) {
      return (
        <div className="mt-1">
          <RCAlertBox type="warning" small>
            {I18n.t('commons.offers.location.localPickupDetails', { location: shop.address.displayLocation })}
          </RCAlertBox>
        </div>
      );
    }

    if (shipping.shippingPrices.length === 0 && !shipping.localPickupOnly) {
      return (
        <div className="mt-1">
          <RCAlertBox type="info" small>
            {I18n.t('commons.offers.location.noShippingCostSet')}
          </RCAlertBox>
        </div>
      );
    }

    return null;
  }

  get offerCurrency() {
    return this.props.isSeller ?
      this.props.data.listing.shop.currency :
      this.props.user.currency;
  }

  get listingPriceInSellerCurrency() {
    if (this.props.data.loading) { return null; }

    const { amountCents, currency } = this.props.data.listing.price;
    return convertMoney({ amountCents, currency }, this.props.data.listing.shop.currency, this.exchangeRates);
  }

  get recommendedPriceInSellerCurrency() {
    if (this.props.data.loading) { return null; }
    if (!this.props.data.listing.priceRecommendation?.priceMiddle?.amountCents) { return null; }

    const { listing } = this.props.data;
    const { amountCents, currency } = listing.priceRecommendation.priceMiddle;

    return convertMoney({ amountCents, currency }, listing.shop.currency, this.exchangeRates);
  }

  get recommendedPriceDropPercent(): number {
    if (!canViewListingPriceRecommendations(this.props.user, this.shopCurrency)) { return null; }
    if (!this.listingPriceInSellerCurrency || !this.recommendedPriceInSellerCurrency) { return null; }
    if ([BRAND_NEW, NON_FUNCTIONING].includes(this.props.data.listing?.condition?.conditionSlug)) { return null; }

    const { amountCents: original } = this.listingPriceInSellerCurrency;
    const { amountCents: suggested } = this.recommendedPriceInSellerCurrency;

    const percentDrop = Math.round(((original - suggested) / original) * 100);

    if (percentDrop < RECOMMENDED_PRICE_DROP_MIN) { return null; }
    if (percentDrop > RECOMMENDED_PRICE_DROP_MAX) { return null; }

    return percentDrop;
  }

  @bind
  handleCancel(e) {
    e.preventDefault();
    this.props.onCancel();
  }

  get submitButtonText() {
    if (!isEmpty(this.offer.price)) {
      return I18n.t('commons.offers.submitOfferButtonWithAmount', {
        offerTotal: offerTotal(this.offer, this.offerCurrency).display,
      });
    }

    return I18n.t('commons.offers.submitOfferButton');
  }

  renderButtons() {
    return (
      <>
        <div className="offers-form__actions">
          <button
            className="offers-form__actions__save"
            type="submit"
            disabled={this.disableSaveButton}
          >
            {this.submitButtonText}
          </button>
          {this.props.onCancel &&
            <button
              className="offers-form__actions__back"
              onClick={this.handleCancel}
              type="button"
            >
              {I18n.t('commons.offers.backAction')}
            </button>
          }
        </div>
      </>
    );
  }

  render() {
    // We "skip" the query using this value, which removes `data`
    // However when we close the modal, it still attempts to render
    if (!this.props.isDisplayed) return null;

    if (this.errorLoading) {
      return (
        <RCAlertBox type="error">
          <p className="mb-0">
            {I18n.t('commons.offers.error')}
          </p>
        </RCAlertBox>
      );
    }

    if (this.isLoading) {
      return (
        <div className="offers-form offers-form--loading">
          <div className="offers-form-placeholder" />
          <div className="offers-form-placeholder" />
          <div className="offers-form-placeholder" />
        </div>
      );
    }

    return (
      <form
        className="offers-form"
        onSubmit={this.handleSubmit}
      >
        <div className="offers-form__header">
          {this.renderListingPrice()}
          {this.renderLocation()}
        </div>
        <OfferCreateFormFields
          error={this.state.error}
          listing={this.props.data.listing}
          exchangeRates={this.exchangeRates}
          currency={this.offerCurrency}
          offer={this.offer}
          onOfferChange={this.handleOfferChange}
          buyerHasAtLeastOneAcceptedOffer={this.buyerHasAtLeastOneAcceptedOffer}
          party={this.negotiationParty}
          // if we show editable location form, offer amount is no longer the first input, and we will instead autofocus
          // the shipping region selector (see components/offers/shipping_region_selector.tsx)
          autoFocus={!this.state.isEditingLocation}
          messageInput={this.props.messageInput}
          showTaxAtCheckoutNotices={this.showTaxAtCheckoutNotice}
          recommendedPriceDropPercent={this.recommendedPriceDropPercent}
        />
        {this.renderButtons()}
      </form>
    );
  }
}

const connectCreateOfferForm: React.SFC<IExternalProps> = flowRight([
  connectCreateOfferFormQuery,
  connectCreateInitialOfferMutation,
])(OfferCreateForm);

export default withUserContext(connectCreateOfferForm);
